import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, formatDate } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Search,
  Package,
  Image as ImageIcon,
  ChevronRight,
  Calendar,
  User,
  Tag,
  ShoppingCart,
  FileText,
  ArrowLeft,
  CheckCircle2,
  Clock,
  AlertTriangle,
} from "lucide-react";

const MILESTONE_LABELS = {
  act_compliance_date: "ACT / Compliance",
  pps_submission_date: "Digital Samples (PPS)",
  ps_submission_date: "Production Sample",
  printing_date: "Printing",
  production_sample_submission_date: "Production Sample Submission",
  pps_delivery_date: "PPS Delivery",
  pps_approval_date: "PPS Approval",
  ps_delivery_date: "PS Delivery",
  ps_approval_date: "PS Approval",
  test_sample_dispatch_date: "Test Sample Dispatch",
  test_report_date: "Test Report",
  inspection_requested_date: "Inspection Requested",
  inspection_date: "Inspection",
  dispatch_date: "Dispatch",
};

const MILESTONE_ORDER = [
  "act_compliance_date",
  "pps_submission_date",
  "ps_submission_date",
  "printing_date",
  "production_sample_submission_date",
  "pps_delivery_date",
  "pps_approval_date",
  "ps_delivery_date",
  "ps_approval_date",
  "test_sample_dispatch_date",
  "test_report_date",
  "inspection_requested_date",
  "inspection_date",
  "dispatch_date",
];

export default function ILMPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemDetail, setItemDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ilm/items`, { withCredentials: true });
      setItems(res.data);
    } catch (err) {
      console.error("Failed to fetch ILM items:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = useCallback(async (q) => {
    setSearch(q);
    try {
      const res = await axios.get(`${API}/ilm/items?q=${encodeURIComponent(q)}`, { withCredentials: true });
      setItems(res.data);
    } catch (err) {
      console.error("Search failed:", err);
    }
  }, []);

  const handleSelectItem = async (itemId) => {
    setDetailLoading(true);
    setSelectedItem(itemId);
    try {
      const res = await axios.get(`${API}/ilm/items/${itemId}`, { withCredentials: true });
      setItemDetail(res.data);
    } catch (err) {
      console.error("Failed to fetch item detail:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-60" />
        <Skeleton className="h-10 w-full max-w-md" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // Detail View
  if (selectedItem && itemDetail) {
    const { item, order_lifecycles } = itemDetail;

    return (
      <div className="space-y-6" data-testid="ilm-detail-page">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => { setSelectedItem(null); setItemDetail(null); }}
            data-testid="ilm-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-3xl tracking-tight">{item.name}</h1>
            <p className="text-muted-foreground mt-0.5 font-mono text-sm">{item.sku}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Standard Info Card */}
          <Card className="border border-border shadow-none rounded-sm lg:col-span-1">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg">Standard Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {item.image_url && (
                <div className="w-full aspect-square bg-muted rounded-sm overflow-hidden">
                  <img
                    src={item.image_url.startsWith("/api") ? `${API.replace("/api", "")}${item.image_url}` : item.image_url}
                    alt={item.name}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                </div>
              )}
              <InfoRow icon={Calendar} label="Created" value={formatDate(item.created_at)} />
              <InfoRow icon={User} label="Created By" value={item.created_by_name || "-"} />
              <InfoRow icon={User} label="Customer" value={item.customer_name || "-"} />
              <InfoRow icon={User} label="Artwork Owner" value={item.artwork_owner || "-"} />
              <InfoRow icon={Tag} label="Category" value={item.category_name || "-"} />
              {item.subcategory_name && (
                <InfoRow icon={Tag} label="Subcategory" value={item.subcategory_name} />
              )}
              <InfoRow icon={FileText} label="Customer Item #" value={item.customer_item_number || "-"} />
              <InfoRow icon={ShoppingCart} label="Total Order Qty" value={item.total_order_qty?.toLocaleString() || "0"} />
              <InfoRow icon={Package} label="No. of Orders" value={item.no_of_orders || "0"} />
            </CardContent>
          </Card>

          {/* Order Lifecycle Table */}
          <Card className="border border-border shadow-none rounded-sm lg:col-span-2">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg">Order Specific Info</CardTitle>
              <CardDescription>{order_lifecycles.length} order(s) with this item</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {order_lifecycles.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Package className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p>No orders found for this item</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/30">
                        <TableHead className="font-mono text-xs uppercase tracking-widest whitespace-nowrap sticky left-0 bg-muted/30 z-10">Order</TableHead>
                        <TableHead className="font-mono text-xs uppercase tracking-widest whitespace-nowrap">Qty</TableHead>
                        <TableHead className="font-mono text-xs uppercase tracking-widest whitespace-nowrap">Order Date</TableHead>
                        {MILESTONE_ORDER.map((key) => (
                          <TableHead key={key} className="font-mono text-xs uppercase tracking-widest whitespace-nowrap text-center">
                            {MILESTONE_LABELS[key]}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {order_lifecycles.map((ol) => (
                        <TableRow key={ol.order_id}>
                          <TableCell className="whitespace-nowrap sticky left-0 bg-background z-10">
                            <div>
                              <span className="font-mono font-medium">{ol.order_number}</span>
                              <div className="text-xs text-muted-foreground">{ol.customer_name}</div>
                            </div>
                          </TableCell>
                          <TableCell className="font-mono whitespace-nowrap">{ol.order_qty}</TableCell>
                          <TableCell className="whitespace-nowrap text-sm">{formatDate(ol.order_date)}</TableCell>
                          {MILESTONE_ORDER.map((key) => {
                            const val = ol.milestones[key];
                            return (
                              <TableCell key={key} className="text-center whitespace-nowrap">
                                {val ? (
                                  <span className="text-xs text-emerald-600 font-medium">{formatDate(val)}</span>
                                ) : (
                                  <span className="text-muted-foreground text-xs">-</span>
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Task Details per Order */}
              {order_lifecycles.map((ol) => (
                ol.tasks && ol.tasks.length > 0 && (
                  <div key={`tasks-${ol.order_id}`} className="border-t px-4 py-3">
                    <p className="text-xs font-mono text-muted-foreground mb-2">Tasks for {ol.order_number}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {ol.tasks.map((task) => (
                        <div
                          key={task.task_id}
                          className="flex items-center gap-2 text-xs p-2 bg-muted/30 rounded-sm"
                        >
                          {task.status === "completed" ? (
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                          ) : task.status === "in_progress" ? (
                            <Clock className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                          ) : (
                            <AlertTriangle className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                          )}
                          <span className="truncate">{task.task_name}</span>
                          <Badge variant="outline" className="text-[10px] ml-auto shrink-0 capitalize">
                            {task.department}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // List View
  return (
    <div className="space-y-6" data-testid="ilm-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl tracking-tight">Item Life Cycle Management</h1>
        <p className="text-muted-foreground mt-1">Track complete lifecycle of items across all orders</p>
      </div>

      <div className="relative max-w-lg">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search by item name, SKU, or customer item number..."
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          className="pl-10 rounded-sm"
          data-testid="ilm-search"
        />
      </div>

      <Card className="border border-border shadow-none rounded-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[60px]"></TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-widest">Item Name</TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-widest">SKU</TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-widest">Customer</TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-widest">Customer Item #</TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-widest">Orders</TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-widest">Total Qty</TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-widest">Created</TableHead>
                <TableHead className="w-[40px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow
                  key={item.item_id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSelectItem(item.item_id)}
                  data-testid={`ilm-item-row-${item.item_id}`}
                >
                  <TableCell>
                    <div className="w-10 h-10 bg-muted rounded-sm overflow-hidden flex items-center justify-center">
                      {item.image_url ? (
                        <img
                          src={item.image_url.startsWith("/api") ? `${API.replace("/api", "")}${item.image_url}` : item.image_url}
                          alt={item.name}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = "none"; e.target.nextSibling && (e.target.nextSibling.style.display = "flex"); }}
                        />
                      ) : (
                        <ImageIcon className="w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell className="font-mono text-sm">{item.sku}</TableCell>
                  <TableCell className="text-sm">{item.customer_name || "-"}</TableCell>
                  <TableCell className="font-mono text-sm text-muted-foreground">{item.customer_item_number || "-"}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="font-mono text-xs">{item.no_of_orders}</Badge>
                  </TableCell>
                  <TableCell className="font-mono text-sm">{item.total_order_qty.toLocaleString()}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{formatDate(item.created_at)}</TableCell>
                  <TableCell>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {items.length === 0 && (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground">No items found</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <Icon className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
    <div className="flex-1">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm font-medium">{value}</p>
    </div>
  </div>
);
